'use client'

import { NEWSLETTER_HONEYPOT_FIELD, useNewsletterSubscribe } from '@/lib/use-newsletter-subscribe'
import { cn } from '@/lib/utils'
import { TurnstileWidget } from './turnstile-widget'

interface Props {
	source?: string
}

export function NewsletterForm({ source = 'blog' }: Props) {
	const { email, setEmail, honey, setHoney, status, errorMsg, turnstileRef, handleSubmit } =
		useNewsletterSubscribe({ placement: 'footer', source })

	if (status === 'success') {
		return (
			<div>
				<h3 className="font-semibold mb-2 text-foreground">Newsletter</h3>
				<p className="text-sm text-green-600 dark:text-green-400">
					Check your inbox to confirm your subscription.
				</p>
			</div>
		)
	}

	return (
		<div>
			<h3 className="font-semibold mb-2 text-foreground">Newsletter</h3>
			<p className="text-sm text-muted-foreground mb-3">
				Get the latest articles and extension insights delivered to your inbox.
			</p>
			<form onSubmit={handleSubmit} className="flex gap-2">
				<input
					type="email"
					placeholder="you@example.com"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					required
					disabled={status === 'submitting'}
					className={cn(
						'flex-1 h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm placeholder:text-muted-foreground outline-none',
						'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
						'disabled:opacity-50 disabled:cursor-not-allowed',
					)}
				/>
				{/* Honeypot — visually + aria hidden. Real users never fill this. */}
				<div
					aria-hidden="true"
					style={{
						position: 'absolute',
						left: '-9999px',
						width: '1px',
						height: '1px',
						overflow: 'hidden',
						opacity: 0,
						pointerEvents: 'none',
					}}
				>
					<label>
						Website (leave empty)
						<input
							type="text"
							name={NEWSLETTER_HONEYPOT_FIELD}
							value={honey}
							onChange={(e) => setHoney(e.target.value)}
							tabIndex={-1}
							autoComplete="off"
						/>
					</label>
				</div>
				<TurnstileWidget ref={turnstileRef} action="newsletter" />
				<button
					type="submit"
					disabled={status === 'submitting'}
					className={cn(
						'h-9 px-4 rounded-md text-sm font-medium',
						'bg-primary text-primary-foreground hover:bg-primary/90',
						'disabled:opacity-50 disabled:pointer-events-none',
					)}
				>
					{status === 'submitting' ? 'Subscribing...' : 'Subscribe'}
				</button>
			</form>
			{status === 'error' && <p className="text-sm text-red-600 dark:text-red-400 mt-2">{errorMsg}</p>}
			<p className="text-xs text-muted-foreground/70 mt-2">
				By subscribing, you agree to receive email updates. Unsubscribe anytime.
			</p>
		</div>
	)
}
